import SessionInfo from './sessionInfo';
import analytics from './analytics';
import Utils from '../utils';
import Constants from '../constants';
import NavigationTracking from './navigationTracking';
import CiblerAPI from './ciblerApi';
import Polyfill from '../polyfills';


export default class Core {
  version = 2;
  checktimeout = 3000;
  sampleCollect = 100;
  sampleAnalytics = 1;
  customerId = 0;
  clientUrl = 'https://cdn.winitout.com/';
  trackOrder = false;
  sentryEnabled = true;
  renderEnabled = true;
  analyticsEnabled = true;
  oauthRedirect;
  userId;
  endpoint;
  initialized;
  eligibilityTrigger = {};

  ciblerAPI;
  sessionInfo;
  navTracking;
  trackingComplete;

  initPageEvent;


  constructor() {
    if (window.WIOCONFIG?.trackingVersion === '0.0.0') return;

    if (!Utils.hasLocalStorage()) {
      Utils.message('localstorage unavailable');
      return;
    }

    Polyfill();

    this.mapConfig();

    this.initPageEvent =
      {
        [Constants.PAGE_TYPE.LOGIN]: e => this.initLoginEvent(e),
        [Constants.PAGE_TYPE.CUSTOMER_ZONE]: e => this.initCZEvent(e),
        [Constants.PAGE_TYPE.ORDER_CONFIRMATION]: e => this.initOrderConfirmationEvent(e),
        [Constants.PAGE_TYPE.HOME]: e => this.initHomeEvent(e),
        [Constants.PAGE_TYPE.PRODUCT_LIST]: e => this.initProductListEvent(e),
        [Constants.PAGE_TYPE.PRODUCT_PAGE]: e => this.initProductEvent(e),
        [Constants.PAGE_TYPE.CART]: e => this.initCartEvent(e),
        [Constants.PAGE_TYPE.SHOWCASE]: e => this.initShowCaseEvent(e),
      };

    const ciblerTriggerCampaign = Utils.getQueryString('ciblerTriggerCampaign');
    const triggerUserGroup = Utils.getQueryString('triggerUserGroup');
    const oauth = Utils.getQueryString('cibler_auth');

    setTimeout(()=> {
      this.init(ciblerTriggerCampaign || triggerUserGroup, !!oauth);
    }, 100);
  }

  mapConfig = () => {
    try {
      const config = window.WIOCONFIG;
      Object.keys(config).forEach((k) => {
        this[k] = config[k];
      });
    } catch (e) {
      Utils.captureException(e);
    }
  };

  init = (trigger, oauth) => {
    try {
      if (!this.initialized) {
        this.initialized = true;
        if (this.sentryEnabled) {
          Utils.initSentry(this.customerId);
        }

        this.oauthRedirect = oauth;

        this.initState();
        this.messageContentFrame();
        this.initContext();
      }
    } catch (err) {
      Utils.captureException(err);
    }
  };

  initContext = () => {
    try {
      if (this.disableGetContext()) return false;
      Utils.tryFunction(this.initTracking, [null], null);
      Utils.tryFunction(this.initEventTrigger, [null], null);
      Utils.tryFunction(this.onTrackingComplete, [null], null);
    } catch (err) {
      Utils.captureException(err);
    }
  };

  initState = () => {
    Utils.debug = Utils.getQueryString('ciblerdebug') === 'true' || false;
    const ep = Utils.getQueryString('ciblerendpoint') || 'PROD';
    const track = Utils.getQueryString('ciblerendpoint') || 'FRONT';
    const order = Utils.getQueryString('ciblerendpoint') || 'ORDER';

    this.userId = Utils.getCookie('cibler_id') || Utils.generateCiblerId('CIB1');
    Utils.createCookie('cibler_id', this.userId, 365);
    this.endpoint = Constants.ENDPOINT[ep];
    this.trackingEndpoint = Constants.ENDPOINT[track];
    this.orderEndpoint = Constants.ENDPOINT[order];

    this.ciblerAPI = new CiblerAPI(this.endpoint, this.trackingEndpoint, this.orderEndpoint, this.customerId, this.userId, this.sampleCollect);
  };

  messageContentFrame = () => {
    this.contentFrame = document.getElementById('wioframe');
    if (this.contentFrame && this.contentFrame.contentWindow && this.contentFrame.contentWindow.postMessage) {
      this.contentFrame.contentWindow.postMessage(JSON.stringify({ owner: 'cibler', ciblerId: this.userId }), '*');
    }
  };

  clearLocalData = () => {
    localStorage.removeItem('ciblerDailyLock');
    localStorage.removeItem('ciblerDailyCampaignLock');
  };

  initTracking = () => {
    this.sessionInfo = new SessionInfo(this);
    this.sessionInfo.loadContext();
    this.navTracking = new NavigationTracking(this);
    if (!this.trackingConsented()) return;

    if (this.analyticsEnabled) { // TODO Replace analytics with non static class
      analytics.create('tag', this.customerId, this.analyticsId, this.userId, this.analyticsEnabled, this.sampleAnalytics);
      analytics.emitTrackEvent('page_view', this.sessionInfo.context.page_type);
    }

    const v = localStorage.getItem('cibler_version');
    localStorage.setItem('cibler_version', `${this.version}`);
    if (!v || (this.version - v) > 1) {
      // Major version change
      this.clearLocalData();
    }
  };

  initEventTrigger = () => {
    this.initCommonTrigger();

    const pageTracking = this.initPageEvent[this.sessionInfo.getPageType()];
    if (pageTracking) {
      Utils.tryFunction(pageTracking, [null], null);
    }
  };

  onTrackingComplete = () => {
    this.trackingComplete = true;
    if (window.WIOCONFIG?.delayRender) return;
    window.WIOCONFIG.trackingComplete = true;
    if (window.WIOCONFIG.callRender) {
      window.WIOCONFIG.callRender(this.customerId);
    }
  };

  /* Event tracking */
  onBasketAdd = (args) => {
    Utils.tryFunction(this.addToBasketHandler, [this.sessionInfo, args], null)
    this.track(Constants.EVENT.ADD_TO_BASKET);
  };

  onPromoCodeError = (code) => {
    this.sessionInfo.context.promoCodeError = !!code;
    this.track(Constants.EVENT.CODE_ERROR, code);
  };

  addPageEventTrigger = (page, func) => {
    this.initPageEvent[page] = func;
  };

  track = (name, action, label,trigger=false) => {
    if (!this.trackingConsented() && name !== Constants.EVENT.order) return;
    if (trigger || this.eligibilityTrigger[name]
      && window.WIORENDER.callEligibility) {
      this.sessionInfo.context.trigger = this.eligibilityTrigger[name];
      window.WIORENDER.callEligibility();
    }

    if (!this.navTracking) {
      this.navTracking = new NavigationTracking(this);
    }

    switch (name) {
      case Constants.EVENT.PAGEVIEW:
        this.navTracking.onPageView();
        break;
      case Constants.EVENT.ADD_TO_BASKET:
        this.sessionInfo.updateCart();
        this.navTracking.onBasketAdd();
        // TODO analytics.addToBasket(this.sessionInfo);
        break;
      case Constants.EVENT.ORDER:
        if (this.analyticsEnabled) {
          analytics.confirmBasket(this.sessionInfo.getTransaction());
        }
        break;
      case Constants.EVENT.CLICK:
        this.navTracking.onClick();
        if (this.analyticsEnabled) {
          analytics.emitTrackEvent(name, action, label);
        }
        break;
      case Constants.EVENT.SUCCESSVIEW:
        this.ciblerAPI.trackSuccessRate(action, false);
        break;
      case Constants.EVENT.SUCCESSCLICK:
        this.ciblerAPI.trackSuccessRate(action, true);
        break;
      case Constants.EVENT.SHOW_OPERATION:
      case Constants.EVENT.ACCEPT_BLOCK:
      case Constants.EVENT.REFUSE_BLOCK:
      case Constants.EVENT.DISCOVER_BLOCK:
        this.sessionInfo.setTrackCampaign(action, name);
        this.ciblerAPI.sessionTrack(label, name);
        this.navTracking.onCampaignTracking(action, name);
        break;
      default:
        this.navTracking.onEvent(name);
        if (this.analyticsEnabled) {
          analytics.emitTrackEvent(name, action, label);
        }
        break;
    }
  };

  getCampaignTracking =  (campaignId) => {
    if (this.sessionInfo && this.sessionInfo.context && this.sessionInfo.context.campaigns)
      return this.sessionInfo.context.campaigns[campaignId];
    return {};

  }


  getLastValidCoupon = () => localStorage.getItem('ciblerValidCoupon');

  saveValidCoupon = (lastCodeTried) => {
    localStorage.setItem('ciblerValidCoupon', lastCodeTried);
  };

  addCustomData = (key, value) => {
    if (!this.sessionInfo.context.customData) this.sessionInfo.context.customData = {};
    this.sessionInfo.context.customData[key] = value;
  };

  /** Interface function * */
  initCallback = () => undefined;

  addToBasketHandler = () => {};

  getAddToBasketElement = () => [];

  getErrorCodeElements = () => false;

  getTrafficSource = () => null;

  getCategoryPath = () => null;

  geoInfo = () => '';

  geolocation = () => '';

  getSponsorId = () => '';

  disableGetContext = () => false;

  getCustomData = () => '';

  getPageType = () => Constants.PAGE_TYPE.UNKNOWN;

  getProductData = () => null;

  getCustomer = () => null;

  getCartData = () => null;

  getOrderInfo = () => null;

  getSessionInfo = () => this.sessionInfo;

  handleExit = (evt) => {
    if (evt.toElement === null && evt.relatedTarget === null) {
      this.navTracking.onExit();
    }
  };

  handleExitMobile = () => {
    if (!this.prevScroll) this.prevScroll = 0;
    if ((this.prevScroll - window.scrollY) > 0) {
      document.removeEventListener('scroll', this.handleExitMobile);
      document.removeEventListener('mouseout', this.handleExitMobile);

      this.navTracking.onExit();
      return;
    }
    this.prevScroll = window.scrollY;
  };

  /** Triggers* */
  initCommonTrigger = () => {
    const elements = this.getAddToBasketElement();
    if (elements) {
      for (let i = 0; i < elements.length; i += 1) {
        elements[i].addEventListener('click', this.onBasketAdd);
      }
    }

    this.track(Constants.EVENT.PAGEVIEW, this.sessionInfo.getPageType());

    if (this.sessionInfo.getDevice() === 'mobile') {
      setTimeout(() => {
        document.addEventListener('scroll', this.handleExitMobile, { passive: true });
      }, 5000);
    }
    setTimeout(() => {
      document.addEventListener('mouseout', this.handleExit, { passive: true });
    }, 5000);
  };

  initOrderConfirmationEvent = () => {
    this.track(Constants.EVENT.ORDER);
    localStorage.removeItem(Constants.cartStorageKey);
    if (this.trackOrder) {
      this.ciblerAPI.sendPostOrder(this.sessionInfo.getTransaction());
    }
  };

  initLoginEvent = () => {};

  initCartEvent = () => {
    this.getErrorCodeElements();
  };

  initProductEvent = () => {
    this.navTracking.onProductPageView();
  };

  initHomeEvent = () => {};

  initProductListEvent = () => {};

  initShowCaseEvent = () => {};

  initCZEvent = () => {};

  trackingConsented = () => true;
}
